<template>
  <div class="dashboard-container p-4 bg-white rounded-lg shadow-sm mb-6">
    <div class="flex justify-between items-center mb-4">
      <h2 class="text-lg font-bold">SEO Recommendations Overview</h2>
      <div class="flex items-center gap-2">
        <v-btn
          color="primary"
          icon
          @click="captureScreenshot"
          title="Save dashboard as image"
        >
          <v-icon>mdi-download-box-outline</v-icon>
        </v-btn>
      </div>
    </div>

    <div v-if="!recommendations.length" class="bg-amber-50 p-6 rounded-lg border border-amber-200 flex flex-col items-center justify-center">
      <v-icon color="amber darken-2" size="36" class="mb-3">mdi-alert-circle-outline</v-icon>
      <p class="text-amber-700 font-medium text-center">No SEO recommendations available</p>
      <p class="text-amber-600 text-sm text-center mt-2">Connect Google Business Profiles to receive personalized recommendations</p>
    </div>

    <div v-else>
      <!-- Recommendation Summary Stats -->
      <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-6">
        <div class="stat-card bg-white border border-slate-200 rounded-lg p-4">
          <div class="flex items-center justify-between">
            <h3 class="text-gray-600 font-semibold">Active Recommendations</h3>
            <v-icon color="primary">mdi-lightbulb-on-outline</v-icon>
          </div>
          <div class="text-2xl font-bold text-primary mt-2">{{ activeRecommendations }}</div>
          <div class="mt-1 text-xs text-gray-500">Ready to implement</div>
        </div>

        <div class="stat-card bg-white border border-slate-200 rounded-lg p-4">
          <div class="flex items-center justify-between">
            <h3 class="text-gray-600 font-semibold">High Priority</h3>
            <v-icon color="red">mdi-flag</v-icon>
          </div>
          <div class="text-2xl font-bold text-red-600 mt-2">{{ highPriorityCount }}</div>
          <div class="mt-1 text-xs text-gray-500">Need immediate attention</div>
        </div>

        <div class="stat-card bg-white border border-slate-200 rounded-lg p-4">
          <div class="flex items-center justify-between">
            <h3 class="text-gray-600 font-semibold">Implemented</h3>
            <v-icon color="success">mdi-check-circle</v-icon>
          </div>
          <div class="text-2xl font-bold text-green-600 mt-2">{{ implementedCount }}</div>
          <div class="mt-1 text-xs text-gray-500">Successfully applied</div>
        </div>

        <div class="stat-card bg-white border border-slate-200 rounded-lg p-4">
          <div class="flex items-center justify-between">
            <h3 class="text-gray-600 font-semibold">Affected Locations</h3>
            <v-icon color="primary">mdi-map-marker-multiple</v-icon>
          </div>
          <div class="text-2xl font-bold text-primary mt-2">{{ affectedLocationsCount }}</div>
          <div class="mt-1 text-xs text-gray-500">Across all recommendations</div>
        </div>
      </div>

      <!-- Recommendation Distribution by Field -->
      <div class="mb-6">
        <h3 class="font-semibold mb-3 text-slate-700">Recommendations by Field</h3>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div class="panel bg-white p-4 rounded-lg border border-slate-200">
            <!-- Pie Chart -->
            <div class="h-56 relative">
              <div v-if="fieldDistribution.length" class="field-distribution-chart">
                <div v-for="(segment, index) in pieSegments" :key="index" 
                     class="pie-segment absolute top-0 left-0 w-full h-full"
                     :style="`clip-path: polygon(50% 50%, ${segment.startX}% ${segment.startY}%, ${segment.endX}% ${segment.endY}%, ${segment.sweepFlag ? '100% 0%, 0% 0%' : '0% 100%, 100% 100%'});
                             background-color: ${getFieldColor(segment.field)};`"
                     @mouseover="highlightedField = segment.field"
                     @mouseleave="highlightedField = null">
                </div>
                <div class="absolute inset-0 flex items-center justify-center">
                  <div class="w-24 h-24 bg-white rounded-full"></div>
                </div>
              </div>
              <div v-else class="flex items-center justify-center h-full">
                <p class="text-gray-500">No field data available</p>
              </div>
            </div>
            
            <!-- Legend -->
            <div class="flex flex-wrap gap-3 mt-4 justify-center">
              <div v-for="field in fieldDistribution" :key="field.field" 
                   class="flex items-center gap-1 px-2 py-1 rounded-md transition-all"
                   :class="{'bg-gray-100': highlightedField === field.field}">
                <div class="w-3 h-3 rounded-full" :style="`background-color: ${getFieldColor(field.field)}`"></div>
                <span class="text-xs">{{ getFieldDisplayName(field.field) }} ({{ field.count }})</span>
              </div>
            </div>
          </div>
          
          <!-- Bar chart of implementation status -->
          <div class="panel bg-white p-4 rounded-lg border border-slate-200">
            <h4 class="text-sm font-medium text-slate-700 mb-3">Implementation Status</h4>
            <div class="space-y-4">
              <div v-for="status in implementationStatus" :key="status.status" class="flex flex-col">
                <div class="flex justify-between items-center mb-1">
                  <span class="text-sm font-medium" :class="getStatusTextColor(status.status)">
                    {{ getStatusDisplayName(status.status) }}
                  </span>
                  <span class="text-xs text-gray-500">{{ status.count }} ({{ getPercentage(status.count, recommendations.length) }}%)</span>
                </div>
                <div class="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div class="h-full rounded-full" 
                       :class="getStatusBarColor(status.status)"
                       :style="`width: ${getPercentage(status.count, recommendations.length)}%`">
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Priority Analysis -->
      <div class="mb-6">
        <h3 class="font-semibold mb-3 text-slate-700">Recommendation Priority Analysis</h3>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div v-for="priority in ['high', 'medium', 'low']" :key="priority"
               class="priority-card bg-white rounded-lg border border-slate-200 p-4 transition-all hover:shadow-md"
               :class="getPriorityCardClass(priority)"
               @click="$emit('filter-priority', priority)">
            <div class="flex items-center gap-2 mb-2">
              <v-icon :color="getPriorityColor(priority)">{{ getPriorityIcon(priority) }}</v-icon>
              <h4 class="font-medium" :class="getPriorityTextClass(priority)">{{ capitalize(priority) }} Priority</h4>
            </div>
            <div class="text-2xl font-bold" :class="getPriorityTextClass(priority)">
              {{ getPriorityCount(priority) }}
            </div>
            <div class="mt-1 text-xs text-gray-500">
              {{ getPriorityDescription(priority) }}
            </div>
            
            <!-- Top field for this priority -->
            <div v-if="getTopFieldForPriority(priority)" class="mt-3 pt-3 border-t border-slate-100">
              <div class="text-xs text-gray-500 mb-1">Most common field:</div>
              <div class="flex items-center gap-1">
                <div class="w-2 h-2 rounded-full" :style="`background-color: ${getFieldColor(getTopFieldForPriority(priority))}`"></div>
                <span class="text-sm font-medium">{{ getFieldDisplayName(getTopFieldForPriority(priority)) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Recent Recommendations -->
      <div>
        <!-- <h3 class="font-semibold mb-3 text-slate-700">Recent Recommendations</h3> -->
        <div v-if="sortedRecommendations.length" class="grid grid-cols-1 gap-3">
          <div v-for="(rec, index) in sortedRecommendations.slice(0, 5)" :key="rec.id" 
              class="top-recommendation p-3 border border-slate-200 rounded-lg hover:bg-slate-50 transition-all"
              @click="$emit('view-recommendation', rec)">
            <div class="flex justify-between">
              <div class="flex-grow">
                <div class="flex items-center gap-2">
                  <div class="font-medium text-slate-800">{{ getRecommendationTitle(rec) }}</div>
                  <v-chip small :color="getPriorityChipColor(rec.priority)" text-color="white" class="text-xs">
                    {{ capitalize(rec.priority || 'medium') }}
                  </v-chip>
                  <v-chip small outlined :color="rec.actionTaken ? 'success' : 'primary'" class="text-xs">
                    {{ rec.actionTaken ? 'Implemented' : 'Active' }}
                  </v-chip>
                </div>
                <div class="text-sm text-slate-600 mt-1">
                  <span v-if="isMultiLocation(rec)">{{ getLocationCount(rec) }} locations</span>
                  <span v-else>{{ rec.location?.name || 'Unknown location' }}</span>
                  <span class="text-xs text-gray-400 mx-1">•</span>
                  <span class="text-xs text-gray-500">{{ formatDate(rec.date) }}</span>
                </div>
              </div>
              <div class="flex items-center">
                <v-btn icon x-small @click.stop="$emit('view-recommendation', rec)">
                  <v-icon>mdi-chevron-right</v-icon>
                </v-btn>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="text-center p-4 text-gray-500">
          No recommendations data available
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import html2canvas from 'html2canvas';

export default {
  name: 'SEORecommendationsDashboard',
  props: {
    recommendations: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      highlightedField: null
    };
  },
  computed: {
    activeRecommendations() {
      return this.recommendations.filter(rec => rec.status === 'active').length;
    },
    
    highPriorityCount() {
      return this.recommendations.filter(rec => rec.priority === 'high').length;
    },
    
    implementedCount() {
      return this.recommendations.filter(rec => rec.actionTaken).length;
    },
    
    affectedLocationsCount() {
      return this.recommendations.reduce((total, rec) => {
        if (rec.location_recommendations) {
          return total + rec.location_recommendations.length;
        }
        return total + (rec.location ? 1 : 0);
      }, 0);
    },
    
    fieldDistribution() {
      const fields = {};
      
      this.recommendations.forEach(rec => {
        const field = rec.field || 'other';
        fields[field] = (fields[field] || 0) + 1;
      });
      
      return Object.entries(fields)
        .map(([field, count]) => ({ field, count }))
        .sort((a, b) => b.count - a.count);
    },
    
    implementationStatus() {
      return [
        { 
          status: 'implemented', 
          count: this.recommendations.filter(rec => rec.actionTaken).length 
        },
        { 
          status: 'active', 
          count: this.recommendations.filter(rec => rec.status === 'active' && !rec.actionTaken).length 
        },
        { 
          status: 'inactive', 
          count: this.recommendations.filter(rec => rec.status !== 'active').length 
        }
      ];
    },
    
    sortedRecommendations() {
      // Sort by date (most recent first)
      return [...this.recommendations].sort((a, b) => {
        const dateA = a.date ? new Date(a.date.seconds * 1000) : new Date(0);
        const dateB = b.date ? new Date(b.date.seconds * 1000) : new Date(0);
        return dateB - dateA;
      });
    },
    
    pieSegments() {
      // Calculate pie chart segments
      const total = this.fieldDistribution.reduce((sum, field) => sum + field.count, 0);
      const segments = [];
      
      let cumulativePercentage = 0;
      
      this.fieldDistribution.forEach(field => {
        const percentage = (field.count / total) * 100;
        const startAngle = cumulativePercentage * 3.6; // 3.6 degrees per percentage point
        const endAngle = (cumulativePercentage + percentage) * 3.6;
        
        // Convert start and end angles to coordinates
        const startX = 50 + 50 * Math.sin(startAngle * Math.PI / 180);
        const startY = 50 - 50 * Math.cos(startAngle * Math.PI / 180);
        const endX = 50 + 50 * Math.sin(endAngle * Math.PI / 180);
        const endY = 50 - 50 * Math.cos(endAngle * Math.PI / 180);
        
        // Determine sweep flag for arc drawing
        const sweepFlag = endAngle - startAngle <= 180;
        
        segments.push({
          field: field.field,
          startX,
          startY,
          endX,
          endY,
          sweepFlag
        });
        
        cumulativePercentage += percentage;
      });
      
      return segments;
    }
  },
  methods: {
    getFieldColor(field) {
      const colors = {
        description: '#4CAF50',
        website: '#2196F3',
        phone: '#9C27B0',
        primary_category: '#FF9800',
        category: '#FF9800',
        name: '#F44336',
        hours: '#607D8B',
        attributes: '#009688',
        other: '#9E9E9E'
      };
      
      return colors[field] || colors.other;
    },
    
    getFieldDisplayName(field) {
      const names = {
        description: 'Description',
        website: 'Website URL',
        phone: 'Phone Number',
        primary_category: 'Primary Category',
        category: 'Category',
        name: 'Business Name',
        hours: 'Business Hours',
        attributes: 'Attributes',
        other: 'Other'
      };
      
      return names[field] || this.capitalize(field);
    },
    
    getStatusBarColor(status) {
      switch (status) {
        case 'implemented':
          return 'bg-green-500';
        case 'active':
          return 'bg-blue-500';
        case 'inactive':
          return 'bg-gray-500';
        default:
          return 'bg-gray-500';
      }
    },
    
    getStatusTextColor(status) {
      switch (status) {
        case 'implemented':
          return 'text-green-600';
        case 'active':
          return 'text-blue-600';
        case 'inactive':
          return 'text-gray-600';
        default:
          return 'text-gray-600';
      }
    },
    
    getStatusDisplayName(status) {
      switch (status) {
        case 'implemented':
          return 'Implemented';
        case 'active':
          return 'Active';
        case 'inactive':
          return 'Inactive';
        default:
          return this.capitalize(status);
      }
    },
    
    getPriorityCount(priority) {
      return this.recommendations.filter(rec => (rec.priority || 'medium') === priority).length;
    },
    
    getPriorityCardClass(priority) {
      switch (priority) {
        case 'high':
          return 'cursor-pointer border-red-200 hover:border-red-300';
        case 'medium':
          return 'cursor-pointer border-amber-200 hover:border-amber-300';
        case 'low':
          return 'cursor-pointer border-blue-200 hover:border-blue-300';
        default:
          return '';
      }
    },
    
    getPriorityTextClass(priority) {
      switch (priority) {
        case 'high':
          return 'text-red-600';
        case 'medium':
          return 'text-amber-600';
        case 'low':
          return 'text-blue-600';
        default:
          return 'text-gray-600';
      }
    },
    
    getPriorityColor(priority) {
      switch (priority) {
        case 'high':
          return 'error';
        case 'medium':
          return 'warning';
        case 'low':
          return 'info';
        default:
          return 'gray';
      }
    },
    
    getPriorityIcon(priority) {
      switch (priority) {
        case 'high':
          return 'mdi-flag';
        case 'medium':
          return 'mdi-flag-outline';
        case 'low':
          return 'mdi-flag-variant-outline';
        default:
          return 'mdi-flag-outline';
      }
    },
    
    getPriorityDescription(priority) {
      switch (priority) {
        case 'high':
          return 'Critical for business visibility';
        case 'medium':
          return 'Important for profile quality';
        case 'low':
          return 'Enhances business profile';
        default:
          return '';
      }
    },
    
    getTopFieldForPriority(priority) {
      const fieldCounts = {};
      
      this.recommendations
        .filter(rec => (rec.priority || 'medium') === priority)
        .forEach(rec => {
          const field = rec.field || 'other';
          fieldCounts[field] = (fieldCounts[field] || 0) + 1;
        });
      
      const sortedFields = Object.entries(fieldCounts)
        .sort((a, b) => b[1] - a[1]);
      
      return sortedFields.length > 0 ? sortedFields[0][0] : null;
    },
    
    getPriorityChipColor(priority) {
      switch (priority) {
        case 'high':
          return 'red';
        case 'medium':
          return 'amber darken-2';
        case 'low':
          return 'blue';
        default:
          return 'grey';
      }
    },
    
    getPercentage(count, total) {
      if (!total) return 0;
      return Math.round((count / total) * 100);
    },
    
    capitalize(str) {
      if (!str) return '';
      return str.charAt(0).toUpperCase() + str.slice(1);
    },
    
    formatDate(date) {
      if (!date) return 'N/A';
      
      let jsDate;
      if (date && typeof date === 'object' && date.seconds) {
        jsDate = new Date(date.seconds * 1000 + (date.nanoseconds || 0) / 1000000);
      } else if (date instanceof Date) {
        jsDate = date;
      } else {
        jsDate = new Date(date);
        if (isNaN(jsDate.getTime())) {
          return 'Invalid date';
        }
      }
      
      return jsDate.toLocaleDateString();
    },
    
    getRecommendationTitle(rec) {
      if (rec.field_name) {
        return this.capitalize(rec.field_name);
      } else if (rec.field) {
        // Handle specific field types with appropriate titles
        switch (rec.field) {
          case 'website':
            return 'Website URL';
          case 'phone':
            return 'Phone Number';
          case 'primary_category':
            return 'Primary Category';
          case 'description':
            return 'Business Description';
          case 'name':
            return 'Business Name';
          default:
            return this.capitalize(rec.field);
        }
      } else if (rec.type === 'category') {
        return 'Category Optimization';
      } else if (rec.type === 'cross_location') {
        return 'Cross-Location Consistency';
      }
      return 'Recommendation';
    },
    
    isMultiLocation(rec) {
      return (rec.location_recommendations && rec.location_recommendations.length > 1);
    },
    
    getLocationCount(rec) {
      if (rec.location_recommendations) {
        return rec.location_recommendations.length;
      }
      return rec.location ? 1 : 0;
    },
    
    async captureScreenshot() {
      try {
        this.$store.commit('showSnackbar', { text: 'Preparing screenshot...', color: 'info' });
        
        const dashboardElement = document.querySelector('.dashboard-container');
        if (!dashboardElement) {
          throw new Error('Dashboard element not found');
        }
        
        const canvas = await html2canvas(dashboardElement, {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff'
        });
        
        const imageData = canvas.toDataURL('image/png');
        const link = document.createElement('a');
        link.href = imageData;
        
        const date = new Date();
        const formattedDate = date.toISOString().split('T')[0];
        link.download = `seo-recommendations-${formattedDate}.png`;
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        this.$snackbar.show("Screenshot saved");
      } catch (error) {
        console.error('Error capturing screenshot:', error);
        this.$snackbar.show("Failed to save screenshot");
      }
    }
  }
};
</script>

<style scoped>
.field-distribution-chart {
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  overflow: hidden;
}

.pie-segment {
  transition: transform 0.2s ease;
}

.pie-segment:hover {
  transform: scale(1.05);
  z-index: 10;
}

.stat-card, .priority-card, .top-recommendation {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.stat-card:hover, .priority-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.top-recommendation:hover {
  transform: translateX(2px);
}

.panel {
  transition: box-shadow 0.2s ease;
}

.panel:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}
</style> 